import { FALLBACK_CONFIG } from './fallback.js';
import { readStored, writeStored } from './storage.js';
/** Short enough that a slow endpoint never delays the bar past first paint. */
const TIMEOUT_MS = 2500;
/** Last good payload per URL, so a remount paints the fresh config, not the fallback. */
const memo = new Map();
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function text(value, fallback) {
    return typeof value === 'string' ? value : fallback;
}
// Unknown fields are dropped rather than carried, so a newer payload can never
// reach the renderer as a shape it does not understand.
function app(raw) {
    if (!isRecord(raw)) {
        return null;
    }
    if (typeof raw.id !== 'string' || raw.id.length === 0) {
        return null;
    }
    return {
        id: raw.id,
        label: text(raw.label, raw.id),
        icon: text(raw.icon, ''),
        href: text(raw.href, ''),
        enabled: raw.enabled === true,
    };
}
/**
 * Parses the catalog, keeping an EMPTY-BUT-VALID list distinguishable from an
 * unusable body. `[]` is a legitimate instruction — an operator who disabled every
 * destination — and must be honoured; `null` means the body could not be read at all
 * and the caller should keep whatever it is already showing.
 *
 * A non-empty array from which not one entry parsed counts as unusable, not as empty:
 * the operator sent apps, we understood none of them, and rendering an empty bar off
 * that is guessing.
 */
function apps(raw) {
    if (!Array.isArray(raw)) {
        return null;
    }
    const parsed = raw.reduce((memoized, entry) => {
        const item = app(entry);
        return item ? [...memoized, item] : memoized;
    }, []);
    if (raw.length > 0 && parsed.length === 0) {
        return null;
    }
    return parsed;
}
/**
 * Coerces an arbitrary JSON body into the payload shape. Never throws: a missing,
 * malformed or newer-versioned field falls back to the embedded default so the
 * bar degrades instead of blanking.
 *
 * `null` is reserved for a body that could not be used, because that is the signal
 * the caller reads as "keep showing what you have". An empty `apps` array is NOT such
 * a body: it is what the endpoint serves when an operator has disabled every
 * destination, and treating it as a failure reverted every host to the compiled-in
 * fallback and painted a hardcoded Home tile — the exact inverse of the instruction.
 *
 * A payload that still carries the retired `theme` or `logo` blocks drops them here like
 * any other unrecognised field, which is what lets a stage serving the old shape run
 * against this renderer while the endpoint change lands separately.
 *
 * This is also the gate every PERSISTED payload passes through (see `restored`). A blob
 * out of localStorage is untrusted input in exactly the way a network body is — it may
 * have been written months ago by a renderer with a different shape — so it gets the same
 * coercion rather than a JSON.parse and a cast.
 */
function normalize(raw) {
    if (!isRecord(raw)) {
        return null;
    }
    const parsed = apps(raw.apps);
    if (!parsed) {
        return null;
    }
    return {
        schema_version: typeof raw.schema_version === 'number' ? raw.schema_version : FALLBACK_CONFIG.schema_version,
        apps: parsed,
    };
}
function cached(url) {
    return memo.get(url);
}
/**
 * The persisted last-good payload, re-validated. Null when there is nothing stored, the
 * store is unreachable, the blob is corrupt, or the entry is past its staleness cap —
 * every one of which means "paint the compiled-in fallback and wait for the fetch".
 *
 * The dependency runs config -> storage and never back, which is why `writeStored` is
 * called from `fetchConfig` here rather than storage.ts reaching for `normalize` itself.
 */
function restored() {
    return normalize(readStored());
}
/**
 * Fetches the config with a hard timeout. Resolves to null on any failure —
 * network, timeout, non-2xx, or an unparseable body — and the caller keeps
 * whatever it is already showing.
 */
async function fetchConfig(url, timeoutMs = TIMEOUT_MS) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        // NEVER set credentials to 'include' or 'same-origin'. The endpoint is served
        // through a bare cors() and returns the LITERAL `Access-Control-Allow-Origin: *`,
        // not a reflected origin. `*` and credentials are mutually exclusive in the
        // browser, so credentialed requests would have every cross-origin response
        // rejected — stranding the bar on the embedded fallback forever, silently, with
        // no console error and no visible failure. 'omit' is the only safe value.
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) {
            return null;
        }
        const next = normalize(await response.json());
        if (!next) {
            return null;
        }
        memo.set(url, next);
        writeStored(next);
        return next;
    }
    catch {
        return null;
    }
    finally {
        clearTimeout(timer);
    }
}
export { cached, fetchConfig, normalize, restored };
//# sourceMappingURL=config.js.map